import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Header from "@/components/Header";
import Dashboard from "@/pages/Dashboard";
import StockDetail from "@/pages/StockDetail";
import CryptoView from "@/pages/CryptoView";
import PortfolioView from "@/pages/PortfolioView";
import WoodrowView from "@/pages/WoodrowView";
import LeaderboardPage from "@/pages/LeaderboardPage";
import VisualizationPage from "@/pages/VisualizationPage";
import LoginPage from "@/pages/LoginPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminPage from "@/pages/AdminPage";
import { ChangelogDialog } from "@/components/ChangelogDialog";
import { AlertNotifier } from "@/components/AlertNotifier";
import { OrderFillNotifier } from "@/components/OrderFillNotifier";
import GuidedTour from "@/components/GuidedTour";
import WhatsNewDialog from "@/components/WhatsNewDialog";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import DisclaimerGate from "@/components/DisclaimerGate";
import LegalPage from "@/pages/LegalPage";
import LearnPage from "@/pages/LearnPage";
import { BackgroundLayer } from "@/components/BackgroundLayer";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PageviewTracker() {
  const location = useLocation();
  useEffect(() => {
    axios.post(`${BACKEND_URL}/api/analytics/pageview`, { page: location.pathname }, { withCredentials: true }).catch(() => {});
  }, [location.pathname]);
  return null;
}

function AppFooter() {
  const [settings, setSettings] = useState({ copyright: "", version: "" });
  const [showLog, setShowLog] = useState(false);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings/public`).then(r => setSettings(r.data || {})).catch(() => {});
  }, []);
  if (!settings.copyright && !settings.version) return null;
  return (
    <footer className="py-4 px-6 text-center border-t border-bt-border-subtle mt-8" data-testid="app-footer">
      <p className="font-mono text-[10px] text-bt-text-tertiary tracking-wider">
        {settings.copyright}
        {" — "}
        <Link to="/legal" className="underline decoration-dotted underline-offset-2 hover:text-bt-gold transition-colors" data-testid="footer-legal-link">
          Disclaimer &amp; Copyright
        </Link>
        {settings.version && (
          <>
            {" — "}
            <button
              onClick={() => setShowLog(true)}
              className="underline decoration-dotted underline-offset-2 hover:text-bt-gold transition-colors"
              title="View release notes"
              data-testid="footer-version-btn"
            >
              {settings.version}
            </button>
          </>
        )}
      </p>
      <ChangelogDialog open={showLog} onOpenChange={setShowLog} />
    </footer>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-bt-bg flex items-center justify-center">
        <div className="font-mono text-bt-text-secondary text-sm">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && !localStorage.getItem("emulating_from")) return <ForcePasswordChange />;
  if (user.disclaimer_pending && !localStorage.getItem("emulating_from")) return <DisclaimerGate />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/learn" element={<LearnPage />} />
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/stock/:symbol" element={<ProtectedRoute><StockDetail /></ProtectedRoute>} />
      <Route path="/crypto" element={<ProtectedRoute><CryptoView /></ProtectedRoute>} />
      <Route path="/portfolio" element={<ProtectedRoute><PortfolioView /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/visualization" element={<ProtectedRoute><VisualizationPage /></ProtectedRoute>} />
      <Route path="/woodrow" element={<ProtectedRoute><WoodrowView /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
    </Routes>
  );
}

function LogoutWarningBanner() {
  const { logoutWarning, resetInactivity } = useAuth();
  if (!logoutWarning) return null;
  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-bt-negative/95 text-white px-6 py-3 flex items-center gap-3 shadow-lg" data-testid="logout-warning">
      <span className="font-mono text-sm">Auto-logout in 1 minute due to inactivity</span>
      <button onClick={resetInactivity} className="px-3 py-1 bg-white text-bt-negative font-mono text-xs font-medium">Stay Logged In</button>
    </div>
  );
}

function AppContent() {
  const { user, loading, isEmulating } = useAuth();
  const showHeader = !loading && user;
  const emulating = showHeader && isEmulating();
  return (
    <>
      {showHeader && <BackgroundLayer id={user?.background} />}
      {showHeader && <Header />}
      {showHeader && <LogoutWarningBanner />}
      <main className={showHeader ? (emulating ? "pt-24" : "pt-16") : ""}>
        <AppRoutes />
      </main>
      {showHeader && <AppFooter />}
      {showHeader && <OrderFillNotifier />}
      {showHeader && <AlertNotifier />}
      {showHeader && <GuidedTour />}
      {showHeader && !user.tour_pending && !user.disclaimer_pending && !user.must_change_password && <WhatsNewDialog key={user.email} />}
    </>
  );
}

function App() {
  return (
    <div className="min-h-screen text-bt-text-primary font-body">
      <BrowserRouter>
        <AuthProvider>
          <PageviewTracker />
          <AppContent />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: 'rgb(var(--bt-surface))',
                border: '1px solid rgb(var(--bt-border-subtle))',
                color: 'rgb(var(--bt-text-primary))',
                fontFamily: "'IBM Plex Sans', sans-serif",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
